import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_FOUR_TEMPERAMENT,
  PERSONALITY_TEST_ASSESSMENT_ID,
  traitTitleKeyMap,
} from "../../utils/general-config";

import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import {
  getCookie,
  getFourTemperamentsPersonalityTestImage,
  getEnneagramBannerImage,
  snakeToTitleCase,
  toCamelCase,
  getFourTemperamentTranslation,
} from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import ExpandableSection from "../../components/expandable-collapsible/component";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const FourTemperamentPersonalityReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();
  // const [content, setContent] = useState(null);
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const { t } = useTranslation();

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const bannerImage = getEnneagramBannerImage();
  const banner1Image = getFourTemperamentsPersonalityTestImage(
    "4-temperments-results-1"
  );
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    // if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }
        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_FOUR_TEMPERAMENT[currentLocale] ||
            MODEL_TYPE_FOUR_TEMPERAMENT[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const dominantTraits = contentData?.dominant;

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
    pink: "from-pink-600 via-pink-500",
  };

  const progressBarColor = [
    "#ff6284",
    "#49A2E6",
    "#805bec",
    "#ffce55",
    "#4cc0c0",
    "#ec6af9",
  ];

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    if (!title) return null;
    const isString = typeof title === "string";
    let firstWord = "";
    let restOfTitle = "";
    if (isString) {
      const words = title.split(" ");
      firstWord = words[0];
      restOfTitle = words.slice(1).join(" ");
    }

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        <img
          src={bannerImage}
          alt={isString ? title : "Banner"}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        <div className="relative h-full flex items-start">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            <div className="flex flex-col  md:flex-row items-center md:items-start justify-between gap-4 pr-10 md:pr-16">
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl font-bold text-white text-left break-words whitespace-normal"
              >
                {isString ? (
                  <span className="inline-block max-w-full md:max-w-none leading-tight">
                    <span className="block sm:inline">{firstWord}</span>
                    {restOfTitle && (
                      <>
                        <span className="hidden sm:inline"> </span>
                        <span className="block sm:inline">{restOfTitle}</span>
                      </>
                    )}
                  </span>
                ) : (
                  title
                )}
              </h2>

              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TemperamentBlendCard = ({ blendData, title }) => {
    if (!blendData || !title) {
      return null;
    }
    const overview = blendData?.overview || "No overview available.";
    const strengthsFlavorText = blendData?.strengths?.FlavorText || "";
    const strengthsBullets = blendData?.strengths?.Bullets || [];
    const challengesFlavorText = blendData?.challenges?.FlavorText || "";
    const challengesBullets = blendData?.challenges?.Bullets || [];
    const growthBullets = blendData?.tipsForGrowth?.Bullets || [];

    const formattedTitleCamelCase = toCamelCase(title);

    return (
      <div className="bg-pink-50 rounded-2xl p-4 md:p-6 mx-auto shadow-md">
        {/* Title */}
        <h1 className="text-pink-600 text-3xl font-bold mb-6">
          {getFourTemperamentTranslation(formattedTitleCamelCase, t)}
        </h1>

        {/* Overview */}
        {overview && <p className="text-gray-600 text-lg mb-6">{overview}</p>}

        {/* Strengths Section */}
        <div className="mb-6">
          <h2 className="text-pink-600 text-2xl font-bold mb-3">
            {t("report.four_temperaments.strengths")}:
          </h2>
          {strengthsFlavorText && (
            <p className="text-gray-600 text-lg mb-3">{strengthsFlavorText}</p>
          )}
          {strengthsBullets?.length > 0 && (
            <ul className="list-disc pl-6">
              {strengthsBullets.map((item, index) => (
                <li key={index} className="text-gray-700 mb-2 text-lg">
                  {item}
                </li>
              ))}
            </ul>
          )}
          {strengthsBullets.length === 0 && (
            <p className="text-gray-500 italic">
              {t("report.four_temperaments.no_strengths_listed")}
            </p>
          )}
        </div>

        {/* Challenges Section */}
        <div className="mb-6">
          <h2 className="text-pink-600 text-2xl font-bold mb-3">
            {t("report.four_temperaments.challenges")}:
          </h2>
          {challengesFlavorText && (
            <p className="text-gray-600 text-lg mb-3">{challengesFlavorText}</p>
          )}
          {challengesBullets.length > 0 && (
            <ul className="list-disc pl-6">
              {challengesBullets.map((item, index) => (
                <li key={index} className="text-gray-700 mb-2 text-lg">
                  {item}
                </li>
              ))}
            </ul>
          )}
          {challengesBullets.length === 0 && (
            <p className="text-gray-500 italic">
              {t("report.four_temperaments.no_growth_tip_available")}
            </p>
          )}
        </div>

        {/* Tips for Growth Section */}
        <div className="mb-2">
          <h2 className="text-pink-600 text-2xl font-bold mb-3">
            {t("report.four_temperaments.tips_for_growth")}:
          </h2>
          {growthBullets.length > 0 && (
            <ul className="list-disc pl-6">
              {growthBullets.map((item, index) => (
                <li key={index} className="text-gray-700 mb-2 text-lg">
                  {item}
                </li>
              ))}
            </ul>
          )}
          {growthBullets.length === 0 && (
            <p className="text-gray-500 italic">
              {" "}
              {t("report.four_temperaments.no_growth_tip_available")}:
            </p>
          )}
        </div>
      </div>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          filter={`url(#shadow-${character || "bullet"}-${actualSize})`}
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const ProgressBar = ({ label, percentage, color }) => {
    return (
      <li>
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="text-sm" style={{ color }}>
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          ></div>
        </div>
      </li>
    );
  };

  const HighlightText = ({ text, highlightWords }) => {
    const traitColors = assignTraitColors(highlightWords); // key: trait.title (EN)

    const localeKey =
      traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
    const localizedTitles = highlightWords.map((trait) => trait[localeKey]);

    const getHighlightedText = () => {
      const regex = new RegExp(`(${localizedTitles.join("|")})`, "gi");

      return text?.split(regex)?.map((part, index) => {
        const matchIndex = localizedTitles.findIndex(
          (title) => title.toLowerCase() === part.toLowerCase()
        );

        if (matchIndex !== -1) {
          const englishKey = highlightWords[matchIndex].title; // use English title for color
          return (
            <span
              key={index}
              style={{
                color: traitColors.get(englishKey),
                fontWeight: "bold",
              }}
            >
              {part}
            </span>
          );
        }

        return part;
      });
    };

    return <p>{getHighlightedText()}</p>;
  };

  const CallToActionSection = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.testMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.discoverMessage")}
            </p>
            <TakeTestButton onClick={onClick1} />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.bigFiveMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.other_assessment_description")}
            </p>
            <TakeTestButton onClick={onClick2} />
          </div>
        </div>
      </section>
    );
  };

  let sortedTraits = [...(userTraits || [])].sort(
    (a, b) => b?.score - a?.score
  );

  let topScore = sortedTraits[0]?.score ?? -1;

  let topUserTraitsArray = sortedTraits.filter(
    (trait) => topScore - trait?.score <= 2
  );

  let updatedDominantTrait = null;
  let blendToBeUsed = null;

  if (topUserTraitsArray.length && contentData) {
    switch (topUserTraitsArray.length) {
      case 1:
        updatedDominantTrait = contentData?.dominant?.single;
        blendToBeUsed = contentData?.singleTemp;
        break;
      case 2:
        updatedDominantTrait = contentData?.dominant?.two;
        blendToBeUsed = contentData?.twoBlend;
        break;
      case 3:
        updatedDominantTrait = contentData?.dominant?.three;
        blendToBeUsed = contentData?.threeBlend;
        break;
      case 4:
        updatedDominantTrait = contentData?.dominant?.four;
        blendToBeUsed = contentData?.fourBlend;
        break;
      default:
        // Optional: handle cases where more than 4 traits fall within the range
        updatedDominantTrait = contentData?.dominant?.four;
        blendToBeUsed = contentData?.fourBlend;
    }
  }

  function getTraitCombinations(traits) {
    if (!Array.isArray(traits) || traits.length === 0) {
      return [];
    }

    const titles = traits
      .map((t) => t?.trait?.title)
      .filter(Boolean)
      .map((title) => title.charAt(0).toUpperCase() + title.slice(1));

    const result = [];

    function generatePermutations(arr, currentCombo = []) {
      if (arr.length === 0) {
        result.push(currentCombo.join("-"));
        return;
      }
      for (let i = 0; i < arr.length; i++) {
        generatePermutations(
          [...arr.slice(0, i), ...arr.slice(i + 1)],
          [...currentCombo, arr[i]]
        );
      }
    }

    generatePermutations(titles);
    return result;
  }

  function getFormattedDominantTraitContent(dominantTraitList, dominantTraits) {
    // Null checks for parameters
    if (!dominantTraitList || !Array.isArray(dominantTraitList)) {
      return null;
    }

    if (!dominantTraits || typeof dominantTraits !== "object") {
      return null;
    }

    const listLength = dominantTraitList.length;

    // Determine which content to use based on the array length
    let result = null;

    if (listLength === 1 && dominantTraits.single) {
      // Check if the required content exists
      if (
        !dominantTraits.single.Content ||
        !dominantTraits.single.RootHeading
      ) {
        return null;
      }

      result = {
        ...dominantTraits.single,
        Content: dominantTraits.single.Content.replace(
          "{Dominant}",
          dominantTraitList[0] || ""
        ),
      };
    } else if (listLength === 2 && dominantTraits.two) {
      // Check if the required content exists
      if (!dominantTraits.two.Content || !dominantTraits.two.RootHeading) {
        return null;
      }

      result = {
        ...dominantTraits.two,
        Content: dominantTraits.two.Content.replace(
          "{Dominant1}",
          dominantTraitList[0] || ""
        ).replace("{Dominant2}", dominantTraitList[1] || ""),
      };
    } else if (listLength === 3 && dominantTraits.three) {
      // Check if the required content exists
      if (!dominantTraits.three.Content || !dominantTraits.three.RootHeading) {
        return null;
      }

      result = {
        ...dominantTraits.three,
        Content: dominantTraits.three.Content.replace(
          "{Dominant1}",
          dominantTraitList[0] || ""
        )
          .replace("{Dominant2}", dominantTraitList[1] || "")
          .replace("{Dominant3}", dominantTraitList[2] || ""),
      };
    } else if (listLength === 4 && dominantTraits.four) {
      // Check if the required content exists
      if (!dominantTraits.four.Content || !dominantTraits.four.RootHeading) {
        return null;
      }

      // For four temperaments, we don't need to replace anything
      result = dominantTraits.four;
    }

    return result;
  }

  const assignTraitColors = (traits) => {
    const traitColors = new Map();

    traits.forEach((trait, index) => {
      const formattedTitle = trait.title; // English title
      if (!traitColors.has(formattedTitle)) {
        traitColors.set(
          formattedTitle,
          progressBarColor[index % progressBarColor.length]
        );
      }
    });

    return traitColors;
  };

  const topTraitArray = getTraitCombinations(topUserTraitsArray);

  if (topUserTraitsArray.length !== 4 && blendToBeUsed) {
    blendToBeUsed = Object.keys(blendToBeUsed || {})
      .filter((key) => topTraitArray.includes(key))
      .reduce((obj, key) => {
        obj[key] = blendToBeUsed[key];
        return obj;
      }, {});
  }

  let dominantTraitList = [];
  if (topUserTraitsArray?.length) {
    dominantTraitList = topUserTraitsArray?.map(
      (trait) =>
        trait?.trait?.title.charAt(0).toUpperCase() +
        trait?.trait?.title.slice(1)
    );
  }

  const allTraits = userTraits.map((item) => {
    return {
      title: item.trait.title,
      title_ES: item.trait.title_ES,
    };
  });

  const progressScore = userTraits?.map((item, index) => {
    const localeKey =
      traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
    return {
      label: snakeToTitleCase(item.trait[localeKey]),
      percentage: item?.percentage,
      color: progressBarColor[index % progressBarColor.length],
    };
  });

  const formattedDominantTraitContent = getFormattedDominantTraitContent(
    dominantTraitList,
    dominantTraits
  );

  const fourTemperamentBlendText = t(
    "report.four_temperaments.four_temperaments_blend"
  );

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                      {t("report.four_temperaments.introduction")}
                    </h2>
                    <HighlightText
                      text={contentData?.intro}
                      highlightWords={allTraits}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}
                      <div>
                        {/* Table Section with better spacing */}
                        <h2 className="text-3xl font-semibold text-gray-700 mt-4 mb-4">
                          {contentData?.yourScore?.RootHeading + " "}
                        </h2>
                        <h2 className="text-2xl font-semibold text-gray-700 my-4">
                          {t("report.four_temperaments.score_summary")}
                        </h2>
                        <ul className="space-y-4">
                          {progressScore?.map((item, index) => {
                            return (
                              <ProgressBar
                                color={item?.color}
                                label={item?.label}
                                percentage={item?.percentage}
                              />
                            );
                          })}
                        </ul>
                      </div>
                      {/* Right Section: Image */}

                      <div className="flex items-center h-[100%] justify-center">
                        <img
                          src={banner1Image}
                          alt="BannerImage"
                          className="w-[95%] h-auto object-contain mt-2 sm:mt-4 md:mt-10"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <DescriptiveCard
                        heading={formattedDominantTraitContent?.RootHeading}
                        overview={formattedDominantTraitContent?.Content}
                        textClr={"text-yellow-700"}
                        bgClr={"bg-yellow-50"}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={updatedDominantTrait?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    {blendToBeUsed && (
                      <TemperamentBlendCard
                        blendData={
                          topUserTraitsArray.length === 4
                            ? blendToBeUsed
                            : blendToBeUsed[Object.keys(blendToBeUsed)[0]]
                        }
                        title={
                          topUserTraitsArray.length === 4
                            ? fourTemperamentBlendText
                            : blendToBeUsed &&
                                Object.keys(blendToBeUsed).length > 0
                              ? Object.keys(blendToBeUsed)[0]
                              : ""
                        }
                      />
                    )}
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.nextSteps?.RootHeading}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p className={`text-lg text-gray-700`}>
                      {contentData?.nextSteps?.Content}
                    </p>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.closing?.RootHeading}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p className={`text-lg text-gray-700 `}>
                      {contentData?.closing?.Content}
                    </p>
                  </div>
                </ExpandableSection>
              </section>

              <CallToActionSection
                onClick1={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
                onClick2={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FourTemperamentPersonalityReport;
